import { MenuItem, Stack, Typography } from '@mui/material';
import { DefinitionList, FormGrid, FormSection, FormTextField } from '../../design-system';
import type { AbsenceConditionCheck, AbsenceConditionFacts, AbsenceConditionPolicy } from './model';
import { conditionLabel, unpaidReasonLabels } from './absenceRequestConditions';
import type { useAbsenceFormValidation } from './useAbsenceFormValidation';

export function AbsenceRequestConditionFields({ policy, facts, onChange, check, validationField, disabled = false, embedded = false }: {
  policy: AbsenceConditionPolicy; facts: AbsenceConditionFacts; onChange: (facts: AbsenceConditionFacts) => void;
  startDate: string; endDate: string; check?: AbsenceConditionCheck; disabled?: boolean; embedded?: boolean;
  validationField: ReturnType<typeof useAbsenceFormValidation>['field'];
}) {
  if (!policy.requestCondition || policy.requestCondition === 'NONE') return null;
  const hospitalization = policy.requestCondition === 'HOSPITALIZATION';
  const content = <>
    {hospitalization ? <>
      {!embedded && <Typography variant="body2" color="text.secondary">{conditionLabel(policy)} · 휴가 사용일은 입원 기간 내 근무일</Typography>}
      <FormGrid columns={1}>
        <FormTextField type="date" required label="입원 시작일" value={facts.hospitalizationStartDate ?? ''} disabled={disabled} onChange={(event) => onChange({ hospitalizationStartDate: event.target.value, hospitalizationEndDate: facts.hospitalizationEndDate })} {...validationField('hospitalizationStartDate')} />
        <FormTextField type="date" required label="퇴원일" value={facts.hospitalizationEndDate ?? ''} disabled={disabled} onChange={(event) => onChange({ hospitalizationStartDate: facts.hospitalizationStartDate, hospitalizationEndDate: event.target.value })} {...validationField('hospitalizationEndDate')} />
      </FormGrid>

    </> : <>
      {!embedded && <Typography variant="body2" color="text.secondary">승인 대기를 반영한 신청 가능 잔액이 0일일 때 신청할 수 있습니다.</Typography>}
      <FormTextField select required label="무급휴가 사유" value={facts.unpaidReason ?? ''} disabled={disabled} onChange={(event) => onChange({ unpaidReason: event.target.value as AbsenceConditionFacts['unpaidReason'] })} {...validationField('unpaidReason')}>
        {Object.entries(unpaidReasonLabels).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
      </FormTextField>
    </>}
    {check && (!embedded || !check.eligible) && <AbsenceConditionCheckSummary check={check} />}
  </>;
  return embedded ? content : <FormSection heading="신청 정보·증빙">{content}</FormSection>;
}

export function AbsenceConditionCheckSummary({ check }: { check: AbsenceConditionCheck }) {
  if (check.requestCondition === 'NONE') return null;
  return <Stack component="section" aria-label="신청 조건 확인" spacing={1}>
    {check.requestCondition === 'UNPAID' && check.prerequisiteBalance !== undefined && <DefinitionList items={[[`${check.prerequisiteTimeOffType?.descriptor || '연차'} 신청 가능 잔액`, `${Number(check.prerequisiteBalance).toLocaleString('ko-KR', { maximumFractionDigits: 4 })}일 (승인 대기 반영)`]]} />}
    {check.messages.map((message) => <Typography key={message} variant="body2" color={check.eligible ? 'text.secondary' : 'error.main'}>{message}</Typography>)}
  </Stack>;
}
